import React, {useCallback, useEffect} from "react";
import Grid from "@material-ui/core/Grid";
import styled from "styled-components";
import {Colors} from "../../theme/colors";
import Chip from "@material-ui/core/Chip";
import {toggleLoader} from "../../store/global/global-slice";
import {PublisherService} from "../../services/publisher.service";
import {useDispatch} from "react-redux";
import {PublisherApplicationModel} from "../../models/publisher/publisher-application.model";
import {ExternalLinkTypeEnum} from "../../models/external-link-type.enum";
import {ApplicationStatusEnum} from "../../models/application-status.enum";
import {Hidden, Tab, Tabs, Tooltip} from "@material-ui/core";
import {useParams} from "react-router-dom";
import {CustomLinearProgress} from "../../components/custom-linear-progress/custom-linear-progress";
import {CustomTabPanel} from "../../components/ui-base/tab-panel";
import emptyLogo from "../../img/empty-publisher-logo.png";
import {SocialMediaIcons} from "../../components/ui-base/social-media-icons";
import {StyledButton} from "../../components/ui-base/button";
import BookStatus from "../../components/publisher/book-status";
import {BookDescriptionDialog} from "../../components/publisher/book-descriptions-dialog";
import {LeftBookDetails} from "../../components/shared/left-book-details";
import {BookmapBasicInfoModel} from "../../models/bookmap-basic-info.model";
import {CustomFileIcon} from "../../components/ui-base/icons/custom-file-icon";
import {CustomHourglassIcon} from "../../components/ui-base/icons/custom-hourglass-icon";
import {HelpOutline} from "@material-ui/icons";
import {useTranslation} from "react-i18next";
import {BaseService} from "../../services/base.service";

const PublisherDetailsBookPage = () => {
    const [openDescriptionDialog, setOpenDescriptionDialog] = React.useState<boolean>(false);
    const [tabActive, setTabActive] = React.useState(0);
    const [application, setApplication] = React.useState<PublisherApplicationModel>(null);
    const [applicationBasicInfo, setApplicationBasicInfo] = React.useState<BookmapBasicInfoModel>(null);
    const dispatch = useDispatch();
    const urlParams = useParams();
    const id: string = urlParams['id'] as string;
    const { t } = useTranslation(['publisher', 'common']);

    const getExternalLinkKey = (name: string) => {
        const linkTypes: string[] = Object.keys(ExternalLinkTypeEnum);

        return linkTypes.find((linkType) => (
            ExternalLinkTypeEnum[linkType] === name
        ));
    }

    const updateStatus = (status: ApplicationStatusEnum) => {
        dispatch(toggleLoader(true));
        loadManuscript(id);
    }

    const loadManuscript = useCallback(async (id: string) => {
        const tmpApplication = await PublisherService.getApplication(id);

        setApplication(tmpApplication);
        dispatch(toggleLoader(false));
    }, [dispatch]);

    const getReadTIme = () => {
        const hours: number = Math.floor(applicationBasicInfo.extra_info.read_time_hours);
        let minutes = 0;
        if ((applicationBasicInfo.extra_info.read_time_hours - hours) > 0) {
            minutes = Math.floor((60 * ((applicationBasicInfo.extra_info.read_time_hours - hours) * 100)) / 100);
        }

        return `${hours}h ${minutes}m`;
    }

    useEffect(() => {

        const loadBasicApp = async (id: string) => {
            const tmpRes = await PublisherService.getApplicationBasicInfo(id);
            setApplicationBasicInfo(tmpRes);
        }

        if (id) {
            dispatch(toggleLoader(true));
            loadManuscript(id);
            loadBasicApp(id);
        }

    }, [id, dispatch, loadManuscript]);

    return (
        <Grid style={{backgroundColor: '#fff', padding: '1rem'}} container direction={"row"}>
            <BookDescriptionDialog
                open={openDescriptionDialog}
                onClose={(value) => {
                    setOpenDescriptionDialog(false);
                }}
                description={application?.manuscript.description}
                summary={application?.manuscript.summary}
                fragment={application?.manuscript.fragment}
            />

            <Hidden mdDown>
                {application &&
                    <LeftBookDetails manuscript={application.manuscript} application={application} updateStatus={(status) => {}}/>
                }
            </Hidden>


            <Grid item container direction={"column"} xs>
                <StyledStatusContainer item container direction={"row"} justifyContent={"space-between"}>
                    <Grid item xs>
                        <StyledStatus>{t('detailsBook.status')} <span>{t(`common:applicationStatus.status${application?.status}`)}</span></StyledStatus>
                    </Grid>

                    <Hidden smDown>
                        <Grid item xs>
                            {application &&
                                <BookStatus status={application.status} id={application.id} bookmarked={application.publisher_bookmarked} updateStatus={updateStatus}/>
                            }
                        </Grid>
                    </Hidden>
                </StyledStatusContainer>

                <Grid item container direction={"row"}>
                    <Grid item container direction={"column"} xs={12} sm={12} md={8} spacing={2}>
                        <Grid item>
                            <StyledH6>{application?.manuscript.title}</StyledH6>
                        </Grid>

                        <Grid item>
                            <StyledDate>{t('detailsBook.created')}: {new Date(application?.created).toLocaleString()}</StyledDate>
                        </Grid>

                        {applicationBasicInfo &&
                        <Grid item container direction={"row"}>
                            {applicationBasicInfo.extra_info?.pub_sheets &&
                            <Grid item xs={true} container direction={"row"} alignItems={"center"}>
                                <CustomFileIcon style={{fill: 'none'}}/>
                                <span style={{fontSize: '0.9rem'}}>{t('detailsBook.pubSheets')}: {Math.round(applicationBasicInfo.extra_info.pub_sheets * 10) / 10}</span>
                            </Grid>
                            }

                            {applicationBasicInfo.extra_info?.read_time_hours !== undefined &&
                            <Grid item xs={true} container direction={"row"} alignItems={"center"}>
                                <CustomHourglassIcon style={{fill: 'none', stroke: '#000'}}/>
                                <span style={{fontSize: '0.9rem'}}>{getReadTIme()}</span>
                            </Grid>
                            }
                        </Grid>
                        }

                        <Grid item>
                            <StyledSpan1>{t('common:categories')}:</StyledSpan1>
                            <Grid container direction={'row'} spacing={1}>
                                {application?.manuscript.categories.map((category) => (
                                    <Grid item key={category.id} style={{maxWidth: '100%'}}>
                                        <StyledCategoryChip size="small" label={category.name} style={{maxWidth: '100%'}} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item>
                            <StyledSpan1>{t('common:tags')}:</StyledSpan1>
                            <Grid container direction={'row'} spacing={1}>
                                {application?.manuscript.tags.filter((tag) => tag.tag_type === 'tag').map((tag) => (
                                    <Grid item key={tag.id} style={{maxWidth: '100%'}}>
                                        <StyledTagChip size="small" label={tag.name} style={{maxWidth: '100%'}}/>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Tabs value={tabActive} onChange={(event, newValue) => { setTabActive(newValue) }}>
                                <Tab label={t('detailsBook.fragment')} style={{fontSize: '0.9rem'}}/>
                                <Tab label={t('detailsBook.desc')} style={{fontSize: '0.9rem'}}/>
                                <Tab label={t('detailsBook.summary')} style={{fontSize: '0.9rem'}}/>
                            </Tabs>

                            <CustomTabPanel value={tabActive} index={0}>
                                {application?.manuscript?.fragment &&
                                    <StyledDescription>{BaseService.cutText(application?.manuscript?.fragment, 1500)}</StyledDescription>
                                }

                                {!application?.manuscript?.fragment &&
                                    <StyledEmptyDescription>{t('detailsBook.fragmentEmpty')}</StyledEmptyDescription>
                                }


                                {application?.manuscript?.fragment.length > 1500 &&
                                    <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
                                        setOpenDescriptionDialog(true);
                                    }}>{t('detailsBook.showFragment')}</StyledButton>
                                }
                            </CustomTabPanel>

                            <CustomTabPanel value={tabActive} index={1}>
                                {application?.manuscript?.description &&
                                <StyledDescription>{BaseService.cutText(application?.manuscript?.description, 1500)}</StyledDescription>
                                }

                                {!application?.manuscript?.description &&
                                <StyledEmptyDescription>{t('detailsBook.descEmpty')}</StyledEmptyDescription>
                                }

                                {application?.manuscript?.description.length > 1500 &&
                                    <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
                                        setOpenDescriptionDialog(true);
                                    }}>{t('detailsBook.showDesc')}</StyledButton>
                                }
                            </CustomTabPanel>

                            <CustomTabPanel value={tabActive} index={2}>
                                {application?.manuscript?.summary &&
                                <StyledDescription>{BaseService.cutText(application?.manuscript?.summary, 1500)}</StyledDescription>
                                }

                                {!application?.manuscript?.summary &&
                                <StyledEmptyDescription>{t('detailsBook.summaryEmpty')}</StyledEmptyDescription>
                                }

                                {application?.manuscript?.summary.length > 1500 &&
                                    <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
                                        setOpenDescriptionDialog(true);
                                    }}>{t('detailsBook.showSummary')}</StyledButton>
                                }
                            </CustomTabPanel>
                        </Grid>
                    </Grid>

                    <Grid item container direction={"column"} xs={12} sm={12} md={4} spacing={1} style={{paddingTop: '1rem'}}>
                        {(application?.matching || application?.matching >= 0) &&
                        <Grid item container direction={"row"} alignItems={"center"} spacing={1}>
                            <Grid item>
                                <StyledPMatching>
                                    {t('detailsBook.matching')}
                                </StyledPMatching>
                            </Grid>

                            <Grid item xs>
                                <CustomLinearProgress
                                    value={BaseService.getMatchingValue(application.matching)}
                                />
                            </Grid>

                            <StyledPercent item>
                                {/*<Tooltip title="Info"><HelpOutline style={{fontSize: '17px', marginTop: '2px'}}/></Tooltip>*/}
                                {/*{Math.round((application.matching || 0) * 100)}%*/}
                            </StyledPercent>
                        </Grid>
                        }
                        {(application?.market_potential || application?.market_potential >= 0) &&
                        <Grid item container direction={"row"} alignItems={"center"} spacing={1}>
                            <Grid item>
                                <StyledPMatching>{t('detailsBook.marketPotential')}</StyledPMatching>
                            </Grid>

                            <Grid item xs>
                                <CustomLinearProgress
                                    value={application.market_potential ? (application.market_potential * 100 / 52) : 0}
                                />
                            </Grid>

                            <StyledPercent item>
                                <Tooltip title={t('detailsBook.marketPotentialDesc')}><HelpOutline style={{fontSize: '17px', marginTop: '2px'}}/></Tooltip>
                                {/*{Math.round((application.market_potential || 0))}%*/}
                            </StyledPercent>
                        </Grid>
                        }
                        <StyledAuthorGrid item container direction={"column"}>
                            <Grid item container direction={"column"} alignItems={"center"} spacing={1}>
                                <Grid item>
                                    <StyledAuthorLogo style={{backgroundImage: `url(${application?.author.photo?.file || emptyLogo})`}}/>
                                </Grid>

                                <Grid item>
                                    <StyledH6Nickname>{application?.author.first_name + " " + application?.author.last_name}</StyledH6Nickname>
                                </Grid>

                                <Grid item container direction={"row"} justifyContent={"center"}>
                                    {application?.author.birth_year &&
                                    <>
                                        <Grid item>
                                            <StyledSpan1>{new Date().getFullYear() - application?.author.birth_year} lat</StyledSpan1>
                                        </Grid>
                                        <Grid item style={{display: 'flex'}}>
                                            <StyledDot/>
                                        </Grid>
                                    </>
                                    }

                                    <Grid item>
                                        <StyledSpan1>{application?.author.residence_place}</StyledSpan1>
                                    </Grid>
                                </Grid>

                                <Grid item container direction="row" spacing={1} justifyContent={"center"}>
                                    {application?.author.external_links.map((externalLink) => (
                                        <Grid item key={externalLink.id}>
                                            <a href={externalLink.url} target="_blank" rel="noreferrer">
                                                <SocialMediaIcons iconName={getExternalLinkKey(externalLink.name)}/>
                                            </a>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            <Grid item container direction={"column"} spacing={1} style={{padding: '0 5%'}}>

                            <Grid item>
                                <StyledAuthorSpan>
                                    {t('common:email')}:
                                </StyledAuthorSpan>
                                <p style={{fontSize: '0.9rem'}}>{application?.author.email || '-'}</p>
                            </Grid>
                            <Grid item>
                                <StyledAuthorSpan>
                                    {t('common:phone')}:
                                </StyledAuthorSpan>
                                <p style={{fontSize: '0.9rem'}}>{application?.author.phone || '-'}</p>
                            </Grid>

                            <Grid item>
                                <StyledAuthorSpan>
                                    {t('detailsBook.aboutMe')}
                                </StyledAuthorSpan>
                                <p style={{fontSize: '0.9rem'}}>{application?.author.bio || '-'}</p>
                            </Grid>

                            </Grid>
                        </StyledAuthorGrid>
                    </Grid>
                </Grid>
            </Grid>

            {/*<AuthorDetailsDialog open={true} handleClose={() => {}}/>*/}
            {/*<PublisherDetailsDialog open={true} handleClose={() => {}}/>*/}
        </Grid>
    )
}

const StyledStatusContainer = styled(Grid)`
  border-bottom: 1px solid #E0E0E0;
`;

const StyledStatus = styled('p')`
  font-size: 0.9rem;
  font-weight: 500;
  padding: 20px 0 15px;
  
  span {
    font-size: 0.8rem;
    padding: 2px 5px;
    border-radius: 10px;
    border: 1px solid #B3DAC7;
    background-color: #EBF5F0;
    color: ${Colors.green};
    margin-left: 5px;
  }
`;

const StyledH6 = styled('h6')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
`;

const StyledH6Nickname = styled('h6')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
  word-break: break-word;
  text-align: center;
`;

const StyledP = styled('p')`
  font-size: 0.9rem;
  font-weight: 500;
`;

const StyledPMatching = styled('p')`
  font-size: 0.9rem;
  font-weight: 600;
  width: 200px;
`;

const StyledPercent = styled(Grid)`
  font-size: 1.1rem;
  font-weight: 500;
`;

const StyledDate = styled('p')`
  color: ${Colors.grayText};
  font-size: 0.8rem;
  font-weight: 400;
`;

const StyledSpan1 = styled('span')`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${Colors.darkGray};
`;

const StyledAuthorSpan = styled('span')`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${Colors.violet};
`;

const StyledCategoryChip = styled(Chip)`
  background-color: #FBF5FF;
  color: ${Colors.lightViolet};
  border: 1px solid ${Colors.lightViolet};
  font-weight: 500;
`;

const StyledTagChip = styled(Chip)`
  background-color: #F5F5F5;
  color: #424242;
  border: 1px solid #C2C2C2;
  font-weight: 500;
`;

const StyledDescription = styled('p')`
  font-size: 0.9rem;
  white-space: pre-line;
`;

const StyledEmptyDescription = styled('p')`
  font-size: 0.9rem;
  white-space: pre-line;
  color: ${Colors.grayText};
`;

const StyledAuthorGrid = styled(Grid)`
  margin-top: 2rem;
  background: #FBF5FF;
  border: 1px solid #E0E0E0;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding-bottom: 2rem;
`;

const StyledAuthorLogo = styled('div')`
  height: 80px;
  width: 80px;
  border-radius: 50%;
  margin-top: -50%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

const StyledDot = styled('div')`
  background-color: ${Colors.grayText};
  width: 4px;
  height: 4px;
  margin: 0 5px;
  border-radius: 50%;
  align-self: center;
`;

export default PublisherDetailsBookPage;
